<script>
// Gives a property agent a portal login by inviting them as a user of this
// account (the same call as Settings → Agents → Add agent). The two stay
// linked only by email, so an email typed here is saved onto the property
// agent too. If that email already belongs to a user, it just links them.
import { useAlert } from 'dashboard/composables';
import { mapActions, mapGetters } from 'vuex';
import { FIELD_CLASS, SELECT_CLASS } from '../constants';
import { findPortalUser } from '../helpers';
import { reportInvalidForm } from '../formFeedback';

// Service agent first: the default role for property agents.
const ROLES = ['service_agent', 'agent', 'administrator'];
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default {
  name: 'PortalAccessModal',
  props: {
    agent: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      fieldClass: FIELD_CLASS,
      selectClass: SELECT_CLASS,
      roles: ROLES,
      form: {
        name: this.agent.name || '',
        email: this.agent.email || '',
        role: ROLES[0],
      },
      emailTaken: false,
      isSubmitting: false,
      submitted: false,
    };
  },
  computed: {
    ...mapGetters('agents', { users: 'getAgents' }),
    trimmedEmail() {
      return this.form.email.trim();
    },
    emailChanged() {
      return (
        this.trimmedEmail.toLowerCase() !==
        String(this.agent.email || '')
          .trim()
          .toLowerCase()
      );
    },
    fieldErrors() {
      let email = '';
      if (!this.trimmedEmail) email = 'REAL_ESTATE.FORM.REQUIRED';
      else if (!EMAIL_PATTERN.test(this.trimmedEmail)) {
        email = 'REAL_ESTATE.AGENTS.ACCESS_INVALID_EMAIL';
      } else if (this.emailTaken) {
        email = 'REAL_ESTATE.AGENTS.ACCESS_EMAIL_TAKEN';
      }
      return {
        name: this.form.name.trim() ? '' : 'REAL_ESTATE.FORM.REQUIRED',
        email,
      };
    },
    isFormValid() {
      return !this.fieldErrors.name && !this.fieldErrors.email;
    },
  },
  watch: {
    'form.email'() {
      this.emailTaken = false;
    },
  },
  methods: {
    ...mapActions('realEstateAgents', ['updateAgent']),
    roleLabel(role) {
      return this.$t(`AGENT_MGMT.AGENT_TYPES.${role.toUpperCase()}`);
    },
    async saveEmailOnAgent() {
      if (!this.emailChanged) return;
      await this.updateAgent({
        agentId: this.agent.agent_id,
        fields: { email: this.trimmedEmail },
      });
    },
    async handleSubmit() {
      this.submitted = true;
      if (!this.isFormValid) {
        reportInvalidForm(this);
        return;
      }
      this.isSubmitting = true;
      try {
        const existing = findPortalUser(
          { email: this.trimmedEmail },
          this.users
        );
        if (existing) {
          await this.saveEmailOnAgent();
          useAlert(
            this.$t('REAL_ESTATE.AGENTS.ACCESS_LINKED', { name: existing.name })
          );
          this.$emit('close');
          return;
        }
        await this.$store.dispatch('agents/create', {
          name: this.form.name.trim(),
          email: this.trimmedEmail,
          role: this.form.role,
        });
        // The invite is out; failing to save the email only loses the link.
        let successKey = 'REAL_ESTATE.AGENTS.ACCESS_SUCCESS';
        try {
          await this.saveEmailOnAgent();
        } catch (err) {
          successKey = 'REAL_ESTATE.AGENTS.ACCESS_SUCCESS_EMAIL_NOT_SAVED';
          // eslint-disable-next-line no-console
          console.error(err);
        }
        useAlert(this.$t(successKey, { email: this.trimmedEmail }));
        this.$emit('close');
      } catch (err) {
        const response = err.response || {};
        const data = response.data || {};
        const attributes = data.attributes || [];
        if (response.status === 422 && !attributes.includes('base')) {
          // Already a user of another account, or otherwise in use.
          this.emailTaken = true;
        } else {
          useAlert(
            data.error ||
              data.message ||
              this.$t('REAL_ESTATE.AGENTS.ACCESS_ERROR')
          );
        }
        // eslint-disable-next-line no-console
        console.error(err);
      } finally {
        this.isSubmitting = false;
      }
    },
  },
};
</script>

<template>
  <div
    class="fixed inset-0 flex items-center justify-center bg-slate-800/80 z-50"
  >
    <div
      class="bg-white dark:bg-slate-900 rounded-lg p-6 shadow-lg w-full max-w-md max-h-[85vh] overflow-y-auto"
    >
      <div class="flex items-center mb-2 justify-between">
        <h1 class="text-xl font-bold text-slate-900 dark:text-slate-100">
          {{ $t('REAL_ESTATE.AGENTS.ACCESS_MODAL_TITLE') }}
        </h1>
        <woot-button
          variant="clear"
          color-scheme="alert"
          icon="dismiss"
          @click="$emit('close')"
        />
      </div>
      <p class="mb-4 text-sm text-slate-600 dark:text-slate-300">
        {{ $t('REAL_ESTATE.AGENTS.ACCESS_MODAL_INTRO') }}
      </p>

      <form class="space-y-4" novalidate @submit.prevent="handleSubmit">
        <div>
          <label class="block mb-1 text-sm text-slate-700 dark:text-slate-200">
            {{ $t('REAL_ESTATE.AGENTS.FIELD_NAME') }}
            <span class="text-red-500">*</span>
          </label>
          <input
            v-model="form.name"
            :placeholder="$t('REAL_ESTATE.AGENTS.FIELD_NAME_PLACEHOLDER')"
            type="text"
            class="reset-base mb-0"
            :class="[
              fieldClass,
              {
                '!border-red-500': submitted && fieldErrors.name,
              },
            ]"
          />
          <p
            v-if="submitted && fieldErrors.name"
            class="mt-1 mb-0 text-xs text-red-500"
          >
            {{ $t(fieldErrors.name) }}
          </p>
        </div>
        <div>
          <label class="block mb-1 text-sm text-slate-700 dark:text-slate-200">
            {{ $t('REAL_ESTATE.AGENTS.ACCESS_FIELD_EMAIL') }}
            <span class="text-red-500">*</span>
          </label>
          <input
            v-model="form.email"
            :placeholder="$t('REAL_ESTATE.AGENTS.FIELD_EMAIL_PLACEHOLDER')"
            type="email"
            class="reset-base mb-0"
            :class="[
              fieldClass,
              {
                '!border-red-500': submitted && fieldErrors.email,
              },
            ]"
          />
          <p
            v-if="submitted && fieldErrors.email"
            class="mt-1 mb-0 text-xs text-red-500"
          >
            {{ $t(fieldErrors.email) }}
          </p>
          <p
            v-else-if="emailChanged && trimmedEmail"
            class="mt-1 mb-0 text-xs text-slate-500 dark:text-slate-400"
          >
            {{ $t('REAL_ESTATE.AGENTS.ACCESS_EMAIL_SAVED_NOTE') }}
          </p>
        </div>
        <div>
          <label class="block mb-1 text-sm text-slate-700 dark:text-slate-200">
            {{ $t('REAL_ESTATE.AGENTS.ACCESS_FIELD_ROLE') }}
          </label>
          <select v-model="form.role" :class="selectClass">
            <option v-for="role in roles" :key="role" :value="role">
              {{ roleLabel(role) }}
            </option>
          </select>
        </div>

        <div
          class="flex justify-end gap-3 pt-4 mt-2 border-t border-slate-200 dark:border-slate-700"
        >
          <woot-button variant="clear" type="button" @click="$emit('close')">
            {{ $t('REAL_ESTATE.AGENTS.CANCEL') }}
          </woot-button>
          <woot-button type="submit" :is-loading="isSubmitting">
            {{ $t('REAL_ESTATE.AGENTS.ACCESS_SUBMIT') }}
          </woot-button>
        </div>
      </form>
    </div>
  </div>
</template>
